/**
 * Malagasy catalog (Latin) for the Phase 2 `MessageId` set — Agent 9, batch 3.
 *
 * `dir: 'ltr'`. Standard Malagasy (Merina) at a mathematician's register. **Warrant →
 * «Fanamarinana»** (justification/verification — flagged). Scientific loanwords are used where
 * Malagasy standardly borrows (logic → Lojika, modal → Modaly, heuristic, scalar, geometry).
 * W/M grades stay Latin (`::numbering-system/latn`). Malagasy does not inflect the noun for
 * number, so the one/other forms coincide. CLDR cardinal categories are one / other (`one`
 * covers 0–1); `frameActive` declares them plus an explicit `=0`. Per INV-STABLE-ID a wrong
 * translation is a presentation concern only — every verdict stays fixed.
 */
import { P2 } from '../message-ids-phase2.js';
export const MG_CATALOG = {
    locale: 'mg',
    dir: 'ltr',
    messages: {
        [P2.inquiryTitle]: 'Fanadihadiana',
        [P2.sectionClaims]: 'Fanambarana',
        [P2.sectionIntegrity]: 'Fahafenoana',
        [P2.axisWarrantLabel]: 'Fanamarinana',
        [P2.axisWarrantValue]: 'W{w, number, ::numbering-system/latn}',
        [P2.axisEmpiricalLabel]: 'Empirika',
        [P2.axisEmpiricalValue]: '{kind, select, defn {Famaritana} interp {Fandikana} testable {Azo andramana} tested {Voasedra} refuted {Voalavo} program {Programan’ny fikarohana} philos {Filozofika} other {Tsy fantatra}}',
        [P2.axisModalLabel]: 'Modaly',
        [P2.axisModalValue]: 'M{m, number, ::numbering-system/latn}',
        [P2.axisKidSummary]: 'Haavon’ny hery {w, number, ::numbering-system/latn}',
        [P2.stateExplorable]: 'Azo jerena',
        [P2.stateRefuted]: 'Voalavo',
        [P2.stateOutside]: 'Ivelan’ny sehatra',
        [P2.verdictConflict]: 'Mifanohitra amin’ny {constraints}',
        [P2.verdictOutside]: 'Mila {missing}',
        [P2.lossRegisterPlain]: 'Natao tsotra ho amin’ny fiteny mahazatra — nesorina ny ampahany amin’ny fahamarinana.',
        [P2.lossRegisterKid]: 'Natao tsotra ho an’ny mpamaky tanora — nesorina ny antsipiriany maro.',
        [P2.lossProfileDetail]: 'Ny mombamomba feno Fanamarinana / Empirika / Modaly dia nofintinina eto.',
        [P2.lossConflictDetail]: 'Nofohezina amin’ity fijery ity ny antsipirian’ny fifandirana.',
        [P2.laneVerified]: 'Voamarina',
        [P2.laneUnverified]: 'Tsy voamarina',
        [P2.laneConflict]: 'Voaporofo ny fifandirana',
        [P2.constraintLogic]: 'Lojika',
        [P2.constraintLightBends]: 'Miolaka ny hazavana ao anatin’ny sahan’ny hery misintona',
        [P2.constraintParallelPostulate]: 'Ny postiolatan’ny tsipika mifanitsy',
        [P2.claimScalarGravity]: 'Teoria skalary momba ny hery misintona',
        [P2.claimNonEuclidean]: 'Jeometria tsy Euklidiana',
        [P2.claimSubstrateSr]: 'Fandikana substrate momba ny fifandraisana manokana',
        [P2.edgeExtObservation]: 'Fandinihana ivelany',
        [P2.edgeResourceSample]: 'Loharano fanisana iombonana',
        [P2.edgeHeuristicSample]: 'Torolalana heuristic',
        [P2.edgeSharedSample]: 'Fototra iombonana',
        // --- Phase 2 view-chrome ---
        [P2.sectionConstraints]: 'Fetra',
        [P2.sectionEdges]: 'Sisiny',
        [P2.sectionFrame]: 'Rafitra',
        [P2.sectionExposure]: 'Fampisehoana',
        [P2.statusAdoptedFormal]: 'Noraisina (ofisialy)',
        [P2.statusAdoptedInterpretive]: 'Noraisina (fandikana)',
        [P2.statusSuspended]: 'Nahantona',
        [P2.edgeEffectConstraint]: 'Mamorona fetra (afaka mandà)',
        [P2.edgeEffectResource]: 'Loharano — tsy misy fiantraikany amin’ny didim-pitsarana',
        [P2.edgeEffectHeuristic]: 'Heuristic — tsy misy fiantraikany amin’ny didim-pitsarana',
        [P2.edgeEffectShared]: 'Fototra iombonana — tsy misy fiantraikany amin’ny didim-pitsarana',
        [P2.frameActive]: '{count, plural, =0 {Tsy misy fetra miasa} one {# fetra miasa} other {# fetra miasa}}',
        [P2.exposureNone]: 'Tsy misy fiankinana amin’ny fandikana',
        [P2.exposureDep]: 'Miankina amin’ny {id}',
        [P2.laneTrackMachine]: 'Milina',
        [P2.laneTrackExpert]: 'Manam-pahaizana',
        [P2.laneTrackCommunity]: 'Vondrom-piarahamonina',
        [P2.expertAudited]: 'Voadinika',
        [P2.expertInProgress]: 'Mandeha',
        [P2.expertNotReviewed]: 'Tsy mbola nojerena',
        [P2.communityFlags]: 'Misy marika',
        [P2.communityNone]: 'Tsy misy marika',
        [P2.controlChallenge]: 'Manohitra',
        [P2.controlActivate]: 'Avelao hiasa',
        [P2.controlMakeInterpretive]: 'Ataovy fandikana',
        [P2.controlImportEdge]: 'Hampidiro sisiny',
        [P2.controlPromote]: 'Asandrato',
        [P2.controlUndo]: 'Avereno',
        [P2.controlRedo]: 'Avereno indray',
        [P2.controlRegister]: 'Hisoratra',
        [P2.promoteRejected]: 'Nolavina ny fampiakarana',
        [P2.promoteAccepted]: 'Nekena ny fampiakarana',
        [P2.promoteReasonRefutationTerminal]: 'Tsy afaka manasitrana fandavana ny porofo.',
        [P2.promoteReasonKeystone]: 'Mila fanohanana empirika ny fampiakarana fototra.',
        [P2.promoteReasonEmpiricalNotFixed]: 'Tsy voafetra ny karazana empirika.',
        [P2.promoteReasonModalNotFixed]: 'Tsy voafetra ny ambaratonga modaly.',
        [P2.promoteReasonWarrantNotImproved]: 'Tsy nihatsara ny ambaratongan’ny fanamarinana.',
        [P2.evidenceComputation]: "{engine} computed {op} = {result} under {config}",
        [P2.lossNumericRounding]: "Result rounded to {dp} decimal places",
    },
};
//# sourceMappingURL=mg.js.map